import React, { useState, useEffect, useRef } from 'react';
import { extractEmailAddress } from '../../utils/email.js';
import { useTranslation } from 'react-i18next';
import {
  useEmailAccounts, useSendEmail, useSaveDraft, useUpdateDraft, useDiscardDraft,
  useUploadAttachment, useDeleteAttachment, useDraftAttachments,
  type AttachmentMeta, type ComposeDraft, type ComposeType, type DraftRow,
} from '../../hooks/useEmail.js';
import { useAccountSignature } from '../../hooks/useSettings.js';
import { Icon } from '../Icon.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Select } from '../ui/Select.js';
import { IconButton } from '../ui/IconButton.js';
import { Drawer } from '../ui/Drawer.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { ContactAutocomplete } from './ContactAutocomplete.js';
import { RichTextEditor, type RichTextEditorHandle } from '../ui/RichTextEditor.js';

interface Props {
  draft: ComposeDraft;
  type: ComposeType;
  onClose: () => void;
  onSent: () => void;
  savedDraftId?: number;
  inline?: boolean;
  onRecipientChange?: (email: string) => void;
}

function stripHtmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function isEmptyHtml(html: string): boolean {
  return !html || html === '<p></p>' || stripHtmlToText(html).trim() === '';
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b}B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)}KB`;
  return `${(b / (1024 * 1024)).toFixed(1)}MB`;
}

function buildSigHtml(sig: string): string {
  if (!sig.trim()) return '';
  const lines = sig.split('\n').map(l =>
    `<p style="margin:0">${l.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') || '<br>'}</p>`
  ).join('');
  return `<div style="margin-top:12px;padding-top:8px;border-top:1px solid var(--evo-stroke);color:var(--evo-fg-3);font-size:12px"><p style="margin:0">-- </p>${lines}</div>`;
}

function parseAddrs(s: string): string[] {
  return s.split(',').map(x => x.trim()).filter(Boolean);
}

export function draftRowToComposeDraft(row: DraftRow): ComposeDraft {
  return {
    accountName: row.accountName,
    to: parseAddrs(row.toAddresses ?? ''),
    cc: parseAddrs(row.ccAddresses ?? ''),
    bcc: parseAddrs(row.bccAddresses ?? ''),
    subject: row.subject ?? '',
    bodyHtml: row.bodyHtml ?? row.bodyText ?? '',
    inReplyTo: row.inReplyTo,
    references: row.referencesHeader ? row.referencesHeader.split(' ').filter(Boolean) : [],
    replyToMessageId: row.replyToMessageId,
  };
}

export function EmailComposer({ draft, type, onClose, onSent, savedDraftId, inline = false, onRecipientChange }: Props) {
  const { t } = useTranslation();
  const { data: accounts = [] } = useEmailAccounts();
  const sendEmail = useSendEmail();
  const saveDraft = useSaveDraft();
  const updateDraftMut = useUpdateDraft();
  const discardDraft = useDiscardDraft();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const uploadAtt = useUploadAttachment();
  const deleteAtt = useDeleteAttachment();

  const [accountName, setAccountName] = useState(draft.accountName || accounts[0] || '');
  const [to, setTo] = useState(draft.to.join(', '));
  const [cc, setCc] = useState(draft.cc.join(', '));
  const [bcc, setBcc] = useState(draft.bcc?.join(', ') ?? '');
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.bodyHtml || '');
  const [quotedHtml] = useState(draft.quotedHtml ?? '');
  const [showCc, setShowCc] = useState(draft.cc.length > 0);
  const [showBcc, setShowBcc] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingEmptySubject, setPendingEmptySubject] = useState(false);
  const [draftId, setDraftId] = useState<number | undefined>(savedDraftId);
  const [draftSaved, setDraftSaved] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentMeta[]>([]);

  const draftIdRef = useRef<number | undefined>(savedDraftId);
  const editorRef = useRef<RichTextEditorHandle>(null);
  const toRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attInitialized = useRef(false);

  const { data: sigData } = useAccountSignature(accountName || undefined);
  const { data: existingAtts = [] } = useDraftAttachments(savedDraftId ?? null);

  const title = type === 'compose' ? t('email.compose.title')
    : type === 'reply' ? 'Reply'
    : type === 'reply-all' ? 'Reply All'
    : 'Forward';

  // Load attachments from an existing draft (only once)
  useEffect(() => {
    if (!attInitialized.current && existingAtts.length > 0) {
      attInitialized.current = true;
      setAttachments(existingAtts);
    }
  }, [existingAtts]);

  // Sync accountName when accounts load
  useEffect(() => {
    if (!accountName && accounts.length > 0) {
      setAccountName(draft.accountName || accounts[0] || '');
    }
  }, [accounts, draft.accountName, accountName]);

  // Keep draftIdRef in sync
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);

  // Focus on mount
  useEffect(() => {
    requestAnimationFrame(() => {
      if (type === 'compose' && toRef.current) {
        toRef.current.focus();
      } else {
        editorRef.current?.focus();
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent of recipient changes (for ClaudePanel compose context)
  useEffect(() => {
    onRecipientChange?.(extractEmailAddress(to));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);

  // Auto-save: 5s debounce after last keystroke
  useEffect(() => {
    if (!to.trim() && !subject.trim() && isEmptyHtml(body)) return;
    const timer = setTimeout(() => {
      const payload = buildDraftPayload();
      const id = draftIdRef.current;
      if (id) {
        updateDraftMut.mutate({ id, ...payload });
      } else {
        saveDraft.mutate(payload, {
          onSuccess: saved => {
            setDraftId(saved.id);
            draftIdRef.current = saved.id;
          },
        });
      }
    }, 5000);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to, cc, bcc, subject, body, accountName]);

  function buildDraftPayload() {
    return {
      accountName,
      toAddresses: to || null,
      ccAddresses: cc || null,
      bccAddresses: bcc || null,
      subject: subject || null,
      bodyHtml: body || null,
      bodyText: stripHtmlToText(body) || null,
      inReplyTo: draft.inReplyTo ?? null,
      referencesHeader: draft.references.join(' ') || null,
      replyToMessageId: draft.replyToMessageId ?? null,
      attachmentIds: attachments.map(a => a.id),
    };
  }

  function handleSaveDraft() {
    if (!accountName) { setError(t('email.compose.no_account_error')); return; }
    setError(null);
    const payload = buildDraftPayload();
    const onErr = (err: unknown) =>
      setError(err instanceof Error ? err.message : t('email.compose.draft_save_error'));
    if (draftId) {
      updateDraftMut.mutate({ id: draftId, ...payload }, {
        onSuccess: () => { setDraftSaved(true); setTimeout(() => setDraftSaved(false), 2000); },
        onError: onErr,
      });
    } else {
      saveDraft.mutate(payload, {
        onSuccess: saved => {
          setDraftId(saved.id);
          draftIdRef.current = saved.id;
          setDraftSaved(true);
          setTimeout(() => setDraftSaved(false), 2000);
        },
        onError: onErr,
      });
    }
  }

  function handleSend() {
    setError(null);
    const toList = parseAddrs(to);
    if (toList.length === 0) { setError(t('email.compose.no_recipient_error')); return; }

    if (type === 'compose' && !subject.trim() && !pendingEmptySubject) {
      setError(t('email.compose.empty_subject_warning'));
      setPendingEmptySubject(true);
      return;
    }
    setPendingEmptySubject(false);

    const sig = sigData?.signature ?? null;
    const sigHtml = sig ? buildSigHtml(sig) : '';
    const fullHtml = (body || '<p></p>') + sigHtml + (quotedHtml ? `<br><br>${quotedHtml}` : '');
    const fullText = stripHtmlToText(fullHtml);

    sendEmail.mutate(
      {
        accountName,
        to: toList,
        cc: parseAddrs(cc),
        bcc: parseAddrs(bcc),
        subject,
        html: fullHtml,
        text: fullText,
        inReplyTo: draft.inReplyTo,
        references: draft.references,
        replyToMessageId: draft.replyToMessageId,
        attachmentIds: attachments.map(a => a.id),
      },
      {
        onSuccess: () => {
          if (draftIdRef.current) discardDraft.mutate(draftIdRef.current);
          onSent();
        },
        onError: (err) => setError(err instanceof Error ? err.message : String(err)),
      }
    );
  }

  async function handleDiscard() {
    if (draftId) {
      const ok = await confirmAction({ title: t('email.compose.discard_draft_confirm'), tone: 'danger' });
      if (!ok) return;
      discardDraft.mutate(draftId);
      onClose();
      return;
    }
    if (to.trim() || subject.trim() || !isEmptyHtml(body)) {
      const ok = await confirmAction({ title: t('email.compose.discard_confirm'), tone: 'warning' });
      if (!ok) return;
    }
    onClose();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) { setError(t('email.compose.attachment_too_large')); continue; }
      const formData = new FormData();
      formData.append('file', file);
      if (draftIdRef.current) formData.append('draftId', String(draftIdRef.current));
      uploadAtt.mutate(formData, {
        onSuccess: att => setAttachments(prev => [...prev, att]),
        onError: (err: Error) => setError(err.message || t('email.compose.attachment_failed')),
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleRemoveAttachment(id: number) {
    deleteAtt.mutate(id, {
      onSuccess: () => setAttachments(prev => prev.filter(a => a.id !== id)),
    });
  }

  const isSavingDraft = saveDraft.isPending || updateDraftMut.isPending;
  const sig = sigData?.signature ?? null;

  const panelStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    background: 'var(--evo-bg)',
  };

  const inner = (
    <div style={panelStyle}>
        {/* Header — fixed height, DS pattern */}
        <div
          className="shrink-0"
          style={{
            height: 'var(--evo-toolbar-height)',
            padding: '0 var(--evo-space-5)',
            borderBottom: '1px solid var(--evo-stroke)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--evo-space-2)',
          }}
        >
          <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-body)', fontWeight: 'var(--evo-fw-medium)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </span>
          <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0, alignItems: 'center' }}>
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              icon={<Icon name="send" size={13} />}
              loading={sendEmail.isPending}
              onClick={handleSend}
            >
              {sendEmail.isPending ? t('email.compose.sending') : t('email.compose.sendBtn')}
            </Button>
            <Button
              variant="outline"
              tone={draftSaved ? 'cyan' : 'muted'}
              size="sm"
              loading={isSavingDraft}
              onClick={handleSaveDraft}
            >
              {isSavingDraft ? '…' : draftSaved ? t('email.compose.draftSaved') : t('email.compose.saveDraft')}
            </Button>
            <Button
              variant="outline"
              tone="muted"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('email.compose.attach')}
            </Button>
            <Button
              variant="outline"
              tone="danger"
              size="sm"
              onClick={() => { void handleDiscard(); }}
            >
              {t('email.compose.discardBtn')}
            </Button>
            <IconButton icon={<Icon name="close" size={16} />} ariaLabel="close" onClick={onClose} variant="ghost" tone="muted" size="sm" />
          </div>
        </div>
        {(error || sendEmail.isSuccess) && (
          <div className="shrink-0 flex items-center gap-2 px-5 py-1" style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
            {error && <span className="text-xs" style={{ color: '#ef4444' }}>{error}</span>}
            {sendEmail.isSuccess && <span className="text-xs" style={{ color: 'var(--evo-cyan)' }}>{t('email.compose.sent')}</span>}
          </div>
        )}

        {/* Fields */}
        <div className="flex flex-col gap-0 shrink-0" style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
          {/* From */}
          <FieldRow label="From">
            <Select
              variant="naked"
              value={accountName || null}
              onChange={v => setAccountName(v ?? '')}
              options={accounts.map(a => ({ value: a, label: a }))}
              size="sm"
            />
          </FieldRow>

          {/* To */}
          <FieldRow label="To">
            <ContactAutocomplete
              inputRef={toRef}
              value={to}
              onChange={setTo}
              placeholder="recipient@example.com"
            />
            <div className="flex gap-1 shrink-0">
              {!showCc && <FieldToggle label="Cc" onClick={() => setShowCc(true)} />}
              {!showBcc && <FieldToggle label="Bcc" onClick={() => setShowBcc(true)} />}
            </div>
          </FieldRow>

          {showCc && (
            <FieldRow label="Cc">
              <ContactAutocomplete value={cc} onChange={setCc} placeholder="cc@example.com" />
              <IconButton variant="ghost" tone="muted" size="sm" ariaLabel="Remove Cc" icon={<Icon name="close" size={12} />} onClick={() => { setShowCc(false); setCc(''); }} />
            </FieldRow>
          )}

          {showBcc && (
            <FieldRow label="Bcc">
              <ContactAutocomplete value={bcc} onChange={setBcc} placeholder="bcc@example.com" />
              <IconButton variant="ghost" tone="muted" size="sm" ariaLabel="Remove Bcc" icon={<Icon name="close" size={12} />} onClick={() => { setShowBcc(false); setBcc(''); }} />
            </FieldRow>
          )}

          {/* Subject */}
          <FieldRow label="Subject">
            <Input variant="naked" value={subject} onChange={setSubject} size="sm" />
          </FieldRow>
        </div>

        {/* Rich text editor */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <RichTextEditor
            ref={editorRef}
            value={body}
            onChange={setBody}
            placeholder={t('email.compose.bodyPlaceholder', { defaultValue: 'Write your message…' })}
            enableHeadings
            enableCodeBlock
          />

          {/* Signature preview */}
          {sig && (
            <div
              className="px-4 py-2 shrink-0"
              style={{
                borderTop: '1px solid var(--evo-stroke)',
                color: 'var(--evo-fg-3)',
                fontSize: '12px',
                lineHeight: 1.5,
                userSelect: 'none',
              }}
            >
              <div style={{ marginBottom: 2 }}>-- </div>
              {sig.split('\n').map((line, i) => (
                <div key={i}>{line || ' '}</div>
              ))}
            </div>
          )}

          {/* Quoted original (reply/forward) — rendered outside Tiptap to preserve full HTML */}
          {quotedHtml && (
            <div
              className="px-4 py-3 shrink-0"
              style={{
                borderTop: '1px solid var(--evo-stroke)',
                overflowY: 'auto',
                maxHeight: '280px',
                background: '#fff',
                fontSize: '13px',
                lineHeight: 1.5,
              }}
              dangerouslySetInnerHTML={{ __html: quotedHtml }}
            />
          )}

          {/* Attachment list */}
          {attachments.length > 0 && (
            <div
              className="flex flex-wrap gap-1.5 px-4 py-2 shrink-0"
              style={{ borderTop: '1px solid var(--evo-stroke)' }}
            >
              {attachments.map(att => (
                <div
                  key={att.id}
                  className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                  style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
                >
                  <span style={{ color: 'var(--evo-fg-2)' }}>{att.filename}</span>
                  <span style={{ color: 'var(--evo-fg-3)' }}>({formatBytes(att.sizeBytes)})</span>
                  <IconButton
                    variant="ghost"
                    tone="muted"
                    size="sm"
                    ariaLabel="remove"
                    icon={<span style={{ fontSize: '11px', lineHeight: 1 }}>✕</span>}
                    onClick={() => handleRemoveAttachment(att.id)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
  );

  if (inline) return inner;

  return (
    <>
      {confirmDialog}
      <Drawer
        open
        onClose={onClose}
        side="right"
        size="md"
        bodyPadding={0}
      >
        {inner}
      </Drawer>
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      className="flex items-center gap-2 px-4"
      style={{ borderBottom: '1px solid var(--evo-stroke)', minHeight: '40px' }}
    >
      <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)', width: '52px' }}>{label}</span>
      {children}
    </div>
  );
}

function FieldToggle({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button variant="outline" tone="muted" size="xs" onClick={onClick}>
      {label}
    </Button>
  );
}

